import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';

const AIAttackReadinessScanner = () => {
  const [target, setTarget] = useState('');
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('form'); // 'form', 'history', 'results'
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/scans/ai-attack-readiness', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setScans(data);
      }
    } catch (err) {
      console.error('Error fetching AI scans:', err);
    }
  };

  const handleScan = async () => {
    if (!target) {
      setError('Por favor ingrese un dominio o IP');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        name: `AI-Attack Scan - ${target}`,
        host: target,
        port_range: "1-1000",
        target_type: "web",
        description: "AI-Attack Readiness Assessment"
      };
      
      const response = await fetch('/api/scans/ai-attack-readiness', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error ejecutando escaneo');
      }

      const data = await response.json();
      setResults(data.results);
      setViewMode('results');
      fetchScans(); // Refresh history
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setResults(scan.results);
    setViewMode('results');
  };

  const getRiskColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' };
    if (score >= 40) return { bg: 'bg-orange-50', border: 'border-orange-500', text: 'text-orange-700', badge: 'bg-orange-500' };
    return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' };
  };

  const getRiskLabel = (riskLevel) => {
    const labels = {
      'LOW': '✅ BAJO RIESGO',
      'MEDIUM': '⚠️ RIESGO MEDIO',
      'HIGH': '🔴 ALTO RIESGO',
      'CRITICAL': '🚨 CRÍTICO'
    };
    return labels[riskLevel] || riskLevel;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800 border-red-300',
      'HIGH': 'bg-orange-100 text-orange-800 border-orange-300',
      'MEDIUM': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'LOW': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          🤖 Defensa contra IAs Maliciosas
        </h1>
        <p className="text-lg text-gray-600">
          Evalúa vulnerabilidades específicamente explotables por IAs maliciosas como FraudGPT, WormGPT, PoisonGPT y XXXGPT
        </p>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setViewMode('form')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'form'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Nuevo Escaneo
        </button>
        <button
          onClick={() => setViewMode('history')}
          className={`px-6 py-3 rounded-lg font-semibold transition-all ${
            viewMode === 'history'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 Historial ({scans.length})
        </button>
      </div>

      {/* Disclaimer Ético */}
      {viewMode === 'form' && (
        <div className="mb-8 bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
          <div className="flex items-start">
            <span className="text-3xl mr-4">ℹ️</span>
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Escaneo Ético y Legal</h3>
              <p className="text-blue-800 text-sm">
                Este escaneo utiliza <strong>tests de DETECCIÓN no invasivos</strong> (no explotación). 
                Solo analiza información públicamente accesible y verifica indicadores de vulnerabilidad.
                Cumple con la Ley 26.388 Argentina (Art. 153 bis) y estándares OWASP.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scan Form */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <TargetSelect
            label="Dominio o IP a Escanear"
            value={target}
            onChange={setTarget}
            types={['domain', 'ip']}
            disabled={scanning}
            testid="ai-attack-target-select"
          />
          <div className="flex gap-4 mt-4">
            <button
              onClick={handleScan}
              disabled={scanning}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {scanning ? '⏳ Escaneando...' : '🚀 Iniciar Escaneo'}
            </button>
          </div>
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              ❌ {error}
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {scanning && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold text-gray-900">Analizando superficie de ataque IA...</p>
          <p className="text-gray-600 mt-2">Esto puede tomar 30-60 segundos</p>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Escaneos AI-Attack Readiness
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => {
                const aiScore = scan.results?.ai_readiness_score || 0;
                const riskLevel = scan.results?.risk_level || 'UNKNOWN';
                return (
                  <div
                    key={scan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 cursor-pointer transition"
                    onClick={() => viewScanDetails(scan)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{scan.target}</h4>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString('es-AR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-2xl font-bold ${getRiskColor(aiScore).text}`}>
                          {aiScore}/100
                        </div>
                        <p className={`text-xs px-2 py-1 rounded ${getRiskColor(aiScore).bg}`}>
                          {getRiskLabel(riskLevel)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && results && (
        <div className="space-y-8">
          {/* Back Button */}
          <button
            onClick={() => setViewMode('history')}
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            ← Volver al historial
          </button>

          {/* Score Principal */}
          <div className={`rounded-lg shadow-xl p-8 border-2 ${getRiskColor(results.ai_readiness_score).border} ${getRiskColor(results.ai_readiness_score).bg}`}>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Preparación contra Ataques IA
              </h2>
              <div className={`text-9xl font-bold mb-4 ${getRiskColor(results.ai_readiness_score).text}`}>
                {results.ai_readiness_score}
                <span className="text-4xl text-gray-500">/100</span>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-8 mb-6">
                <div
                  className={`h-8 rounded-full transition-all duration-500 ${getRiskColor(results.ai_readiness_score).badge} flex items-center justify-end pr-4`}
                  style={{ width: `${results.ai_readiness_score}%` }}
                >
                  <span className="text-white font-bold text-sm">{results.ai_readiness_score}%</span>
                </div>
              </div>

              {/* Risk Badge */}
              <div className={`inline-block px-8 py-4 rounded-full font-bold text-2xl ${getRiskColor(results.ai_readiness_score).badge} text-white shadow-lg`}>
                {getRiskLabel(results.risk_level)}
              </div>
            </div>
          </div>

          {/* Attack Vectors */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="text-3xl mr-3">🎯</span>
              Vectores de Ataque IA
            </h3>
            <div className="space-y-4">
              {Object.entries(results.attack_vectors).map(([vector, score]) => {
                const maxScores = {
                  phishing_surface: 40,
                  injection_points: 30,
                  auth_weaknesses: 40,
                  info_disclosure: 30,
                  automation_friendly: 60,
                  api_chain_risk: 50
                };
                const max = maxScores[vector] || 100;
                const percentage = Math.min(100, (score / max) * 100);
                const isHigh = percentage > 60;

                const vectorNames = {
                  phishing_surface: '🎣 Superficie de Phishing (FraudGPT)',
                  injection_points: '💉 Puntos de Inyección (WormGPT)',
                  auth_weaknesses: '🔐 Debilidades de Autenticación',
                  info_disclosure: '📄 Divulgación de Información',
                  automation_friendly: '🤖 Facilidad de Automatización',
                  api_chain_risk: '🔗 Cadenas de APIs (Agentes IA Autonomos)'
                };

                return (
                  <div key={vector} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-900">{vectorNames[vector]}</span>
                      <span className={`font-bold ${isHigh ? 'text-red-600' : 'text-green-600'}`}>
                        {score}/{max}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className={`h-4 rounded-full transition-all duration-500 ${isHigh ? 'bg-red-500' : 'bg-green-500'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Vulnerabilidades/Riesgos Detectados */}
          {results.vulnerabilities && results.vulnerabilities.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">⚠️</span>
                Vulnerabilidades y Riesgos Detectados ({results.vulnerabilities.length})
              </h3>
              <div className="space-y-4">
                {results.vulnerabilities.map((vuln, index) => (
                  <div key={index} className={`border-2 rounded-lg p-6 ${getSeverityColor(vuln.severity.toUpperCase())}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(vuln.severity.toUpperCase())}`}>
                            {vuln.severity.toUpperCase()}
                          </span>
                          <h4 className="text-lg font-bold text-gray-900">{vuln.title}</h4>
                        </div>
                        <p className="text-gray-700 whitespace-pre-line mb-4">{vuln.description}</p>
                        
                        {vuln.ai_exploitation && (
                          <div className="bg-white bg-opacity-50 rounded-lg p-4 mb-4">
                            <p className="text-sm font-semibold text-gray-900 mb-1">🤖 Explotación por IA:</p>
                            <p className="text-sm text-gray-800">{vuln.ai_exploitation}</p>
                          </div>
                        )}

                        {vuln.recommendation && (
                          <div className="bg-white bg-opacity-50 rounded-lg p-4">
                            <p className="text-sm font-semibold text-gray-900 mb-1">💡 Recomendación:</p>
                            <p className="text-sm text-gray-800">{vuln.recommendation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analisis de Cadenas de APIs */}
          {results.detailed_results?.api_chain_analysis && (
            <APIChainSection chainData={results.detailed_results.api_chain_analysis} />
          )}

          {/* Recomendaciones Priorizadas */}
          {results.recommendations && results.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">💡</span>
                Recomendaciones Priorizadas
              </h3>
              <div className="space-y-4">
                {results.recommendations.map((rec, index) => {
                  const priorityColors = {
                    'CRITICAL': 'bg-red-500',
                    'HIGH': 'bg-orange-500',
                    'MEDIUM': 'bg-yellow-500',
                    'LOW': 'bg-blue-500'
                  };
                  return (
                    <div key={index} className="border-2 border-gray-200 rounded-lg p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <span className={`flex-shrink-0 w-10 h-10 ${priorityColors[rec.priority]} text-white rounded-full flex items-center justify-center font-bold text-lg`}>
                          {index + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-3 py-1 ${priorityColors[rec.priority]} text-white rounded-full text-xs font-bold`}>
                              {rec.priority}
                            </span>
                            <h4 className="text-xl font-bold text-gray-900">{rec.title}</h4>
                          </div>
                          <p className="text-gray-700 mb-4">{rec.description}</p>

                          {/* Steps */}
                          {rec.steps && rec.steps.length > 0 && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <p className="font-semibold text-gray-900 mb-2">Pasos a seguir:</p>
                              <ul className="space-y-1 text-sm text-gray-800">
                                {rec.steps.map((step, i) => (
                                  <li key={i}>{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Métricas */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-3">
                              <p className="text-xs text-blue-600 font-semibold mb-1">Timeline</p>
                              <p className="text-sm font-bold text-blue-900">{rec.timeline}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-3">
                              <p className="text-xs text-green-600 font-semibold mb-1">Costo</p>
                              <p className="text-sm font-bold text-green-900">{rec.cost}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3">
                              <p className="text-xs text-purple-600 font-semibold mb-1">Impacto</p>
                              <p className="text-sm font-bold text-purple-900">{rec.ai_defense_impact}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Contexto de Amenazas IA */}
          {results.threat_intelligence && (
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg shadow-lg p-6 border-2 border-purple-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="text-3xl mr-3">🚨</span>
                Contexto de Amenazas IA Actuales
              </h3>

              {/* IAs Maliciosas Activas */}
              {results.threat_intelligence.active_ai_threats && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">IAs Maliciosas Activas:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {results.threat_intelligence.active_ai_threats.map((threat, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-purple-200">
                        <h5 className="font-bold text-purple-900 text-lg mb-2">{threat.name}</h5>
                        <p className="text-sm text-gray-700 mb-2">{threat.capability}</p>
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-600">Precio: {threat.price}</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">{threat.target_sectors.join(', ')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Incidentes Recientes */}
              {results.threat_intelligence.recent_incidents && (
                <div className="bg-white bg-opacity-50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Incidentes Recientes:</h4>
                  <ul className="space-y-2 text-sm text-gray-800">
                    {results.threat_intelligence.recent_incidents.map((incident, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">🔴</span>
                        <span>{incident}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


/* API Chain Analysis Section */
const APIChainSection = ({ chainData }) => {
  if (!chainData) return null;
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="api-chain-section">
      <h3 className="text-xl font-bold text-gray-900 flex items-center mb-4">
        <span className="text-2xl mr-2">🔗</span>
        Analisis de Cadenas de APIs (Agentic AI Attack Surface)
      </h3>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-gray-50 rounded p-3 text-center">
          <div className={`text-2xl font-bold ${chainData.risk_level === 'CRITICAL' ? 'text-red-600' : chainData.risk_level === 'HIGH' ? 'text-orange-600' : 'text-green-600'}`}>
            {chainData.risk_level}
          </div>
          <div className="text-xs text-gray-500 mt-1">Riesgo de Cadena</div>
        </div>
        <div className="bg-gray-50 rounded p-3 text-center">
          <div className={`text-2xl font-bold ${chainData.api_docs_exposed ? 'text-red-600' : 'text-green-600'}`}>
            {chainData.api_docs_exposed ? 'EXPUESTA' : 'OCULTA'}
          </div>
          <div className="text-xs text-gray-500 mt-1">Doc. API</div>
        </div>
        <div className="bg-gray-50 rounded p-3 text-center">
          <div className="text-2xl font-bold text-gray-800">{chainData.unauthenticated_endpoints?.length || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Endpoints Sin Auth</div>
        </div>
        <div className="bg-gray-50 rounded p-3 text-center">
          <div className="text-2xl font-bold text-gray-800">{chainData.chain_attack_scenarios?.length || 0}</div>
          <div className="text-xs text-gray-500 mt-1">Escenarios de Ataque</div>
        </div>
      </div>

      {chainData.chain_attack_scenarios && chainData.chain_attack_scenarios.length > 0 && (
        <div className="space-y-3 mb-4">
          <h4 className="font-semibold text-gray-700">Escenarios de Ataque Encadenado Detectados:</h4>
          {chainData.chain_attack_scenarios.map((scenario, idx) => (
            <div key={idx} className={`border rounded-lg p-4 ${
              scenario.risk === 'CRITICAL' ? 'border-red-300 bg-red-50' : 
              scenario.risk === 'HIGH' ? 'border-orange-300 bg-orange-50' : 'border-gray-200'
            }`}>
              <div className="flex justify-between items-start mb-2">
                <span className="font-bold text-gray-900">{scenario.name}</span>
                <span className={`px-2 py-1 rounded text-xs font-bold text-white ${
                  scenario.risk === 'CRITICAL' ? 'bg-red-600' : 'bg-orange-500'
                }`}>{scenario.risk}</span>
              </div>
              <p className="text-sm text-gray-700 mb-2">{scenario.description}</p>
              <div className="flex gap-4 text-xs text-gray-500">
                <span>Pasos: {scenario.steps}</span>
                <span>Herramientas: {scenario.ai_tools?.join(', ')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {chainData.api_doc_urls && chainData.api_doc_urls.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
          <span className="text-sm font-medium text-red-800">API Docs Expuestas: </span>
          <span className="text-sm text-red-700">{chainData.api_doc_urls.join(', ')}</span>
        </div>
      )}

      {chainData.graphql_introspection && (
        <div className="bg-red-50 border border-red-200 rounded p-3">
          <span className="text-sm font-bold text-red-800">GraphQL Introspection ACTIVO</span>
          <span className="text-sm text-red-700 ml-2">— Schema completo accesible para agentes IA</span>
        </div>
      )}
    </div>
  );
};

export default AIAttackReadinessScanner;
